import {NativeImageSource} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeCreateImage} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/create-image/node"
import {ImageOpSystem} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-system"
import {computeLabStats} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/lab-stats-subgraph"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {TexturesApiService} from "@app/textures/service/textures-api.service"
import {decodeTiffToImageData} from "@common/helpers/utils/decode-tiff"

// Decodes an uploaded image file and runs it through the same Lab-space mean/variance
// computation the recolor operator uses for its own sourceStats (see compute-source-lab-stats.ts
// and lab-stats-subgraph.ts), entirely in a throwaway ImageOpNodeBag that never touches the
// operator-stack pipeline.
export async function computeTargetStatsFromImageFile(
    file: File,
    imageOpSystem: ImageOpSystem,
    texturesApi: TexturesApiService,
): Promise<TextureEditNodes.OperatorRecolor["targetStats"]> {
    if (file.name.toLowerCase().endsWith(".exr")) {
        // decodeEXR runs entirely client-side (WASM worker), no backend round trip
        const decoded = await texturesApi.decodeEXR(await file.arrayBuffer())
        if (decoded.dataType !== "float32") {
            throw new Error(`Unsupported EXR data type: ${decoded.dataType}`)
        }
        // decoded.data comes back as a Uint8Array view over the underlying float32 bytes (same as
        // image-op-data-object-page-supplier-full-image.ts's convertToImageData), not an actual
        // Float32Array instance — reinterpret the same buffer instead of casting it.
        const data = new Float32Array(
            decoded.data.buffer,
            decoded.data.byteOffset,
            decoded.data.byteLength / Float32Array.BYTES_PER_ELEMENT,
        ) as Float32Array<ArrayBuffer>
        return computeTargetStatsFromPixelData(imageOpSystem, {
            width: decoded.width,
            height: decoded.height,
            channels: decoded.channelLayout.length,
            data,
        })
    } else {
        // Browsers don't reliably decode TIFF via createImageBitmap(file), so decode it ourselves
        // (same client-side, no-backend-round-trip approach as EXR above) and hand off an ImageData
        // instead. No color space info is read from the TIFF - it's assumed to be sRGB, same as any
        // other non-EXR format here.
        const fileName = file.name.toLowerCase()
        const bitmap =
            fileName.endsWith(".tif") || fileName.endsWith(".tiff")
                ? await createImageBitmap(decodeTiffToImageData(new Uint8Array(await file.arrayBuffer())))
                : await createImageBitmap(file)
        try {
            return await computeTargetStatsFromPixelData(imageOpSystem, {
                width: bitmap.width,
                height: bitmap.height,
                channels: 4,
                data: new NativeImageSource(true, bitmap),
            })
        } finally {
            bitmap.close()
        }
    }
}

async function computeTargetStatsFromPixelData(
    imageOpSystem: ImageOpSystem,
    image: {width: number; height: number; channels: number; data: Float32Array<ArrayBuffer> | NativeImageSource},
): Promise<TextureEditNodes.OperatorRecolor["targetStats"]> {
    const bag = new ImageOpNodeBag("recolorTargetStatsFromImage")
    bag.beginFrame()
    const createdImage = bag.getNode("createImage", ImageOpNodeCreateImage, {
        descriptor: {width: image.width, height: image.height, channels: image.channels},
        data: image.data,
    })
    bag.endFrame()

    try {
        // the native upload above already performs the sRGB -> linear conversion on the GPU, and
        // decoded EXR data is natively linear — either way the created image is "linear" downstream,
        // same as a normal sourceImage
        return await computeLabStats(imageOpSystem, createdImage, "linear", image.channels === 1)
    } finally {
        bag.dispose()
    }
}
